import React, { useState } from 'react';
import { BrainCircuit, CheckCircle2, Zap, AlertTriangle, Activity, Heart, Moon, NotebookPen, UserCircle } from 'lucide-react';
import CoachNotesModal from './CoachNotesModal';
import AthleteProfileModal from './AthleteProfileModal';
import AthleteTagsModal from './AthleteTagsModal';

const AthleteHeader = ({ athlete, recoveryData, onAiClick }) => {
  const [showNotes, setShowNotes] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [athleteTags, setAthleteTags] = useState(() => athlete?.tags || []);

  if (!athlete) return null;

  // 1. Identity Data
  const avatarUrl = athlete.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(athlete.name)}&background=random`;
  
  // Priorizar el readinessScore profundo si ya ha cargado, si no, usar el básico de la tabla
  const readiness = recoveryData?.readinessScore !== undefined ? recoveryData.readinessScore : (athlete.readinessScore || 0);
  
  let dynamicStatus = athlete.recentStatus;
  if (readiness >= 80) dynamicStatus = 'Óptimo';
  else if (readiness >= 65) dynamicStatus = 'Recuperación';
  else if (readiness > 0) dynamicStatus = 'Fatiga Alta';

  const statusColor = { 'Fatiga Alta': '#ef4444', 'Recuperación': '#f59e0b', 'Óptimo': 'var(--health-green)', 'Sin datos': 'rgba(255,255,255,0.2)' }[dynamicStatus] || 'rgba(255,255,255,0.2)';
  
  // Safe extraction of recovery metrics
  let hrvDisplay = athlete.hrvDisplay || '--';
  let sleepDisplay = athlete.sleepDisplay || '--';

  // 3. AI Heuristic Verdict
  let aiVerdict;
  let AiIcon;
  let aiColor;

  if (athlete?.preferences?.recoveryMode) {
    AiIcon = Heart;
    aiColor = '#10b981';
    aiVerdict = `${athlete.name.split(' ')[0]} se encuentra en Modo Recuperación / Lesión. La prioridad principal ahora mismo es la regeneración fisiológica y la fisioterapia.`;
  } else if (readiness >= 80) {
    AiIcon = CheckCircle2;
    aiColor = 'var(--health-green)';
    aiVerdict = `${athlete.name.split(' ')[0]} ha asimilado bien el entrenamiento reciente y su recuperación es óptima. Día ideal para sesiones exigentes o de alta intensidad.`;
  } else if (readiness >= 50) {
    AiIcon = Zap;
    aiColor = '#f59e0b'; // Amber
    aiVerdict = `Recuperación moderada. El cuerpo de ${athlete.name.split(' ')[0]} sigue asimilando carga. Se recomienda mantener el plan o aplicar ligeros ajustes de volumen.`;
  } else if (readiness > 0) {
    AiIcon = AlertTriangle;
    aiColor = '#ef4444'; // Red
    aiVerdict = `Atención: Niveles de fatiga altos. El sistema nervioso central está estresado. Considera fuertemente cambiar la sesión de hoy por recuperación activa o descanso total.`;
  } else {
    AiIcon = BrainCircuit;
    aiColor = 'var(--text-muted)';
    if (athlete.lastActivity && athlete.lastActivity !== 'Sin actividad reciente') {
      aiVerdict = `No hay datos biométricos (sueño/HRV) para evaluar la recuperación fisiológica, pero consta entrenamiento reciente (${athlete.lastActivity.split('-')[0].trim()}). Monitoriza sensaciones.`;
    } else {
      aiVerdict = `No hay suficientes datos biométricos ni de actividad reciente para evaluar el estado actual del atleta.`;
    }
  }

  return (
    <div style={{
      marginBottom: '2rem',
      padding: '2rem',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '2rem',
      background: `linear-gradient(145deg, rgba(20,20,20,0.8) 0%, rgba(10,10,10,0.9) 100%)`,
      borderRadius: '24px',
      border: '1px solid rgba(255,255,255,0.05)',
      boxShadow: `0 10px 40px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)`,
      position: 'relative',
      overflow: 'hidden',
      animation: 'fadeInDown 0.4s ease-out'
    }}>
      {/* Background Glow */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        left: '-10%',
        width: '50%',
        height: '200%',
        background: `radial-gradient(circle at center, ${statusColor}15 0%, transparent 70%)`,
        pointerEvents: 'none',
        zIndex: 0
      }} />
      
      {/* Columna 1: Identidad y Acciones */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', zIndex: 1, flex: '1 1 280px' }}>
        <div style={{ position: 'relative' }}>
          <img 
            src={avatarUrl} 
            alt={athlete.name} 
            style={{ 
              width: '90px', height: '90px', borderRadius: '50%', 
              border: `3px solid ${statusColor}`, objectFit: 'cover',
              boxShadow: `0 0 20px ${statusColor}40`
            }}
          />
          {/* Status Indicator Dot */}
          <div style={{
            position: 'absolute', bottom: '5px', right: '5px', width: '18px', height: '18px',
            background: statusColor, borderRadius: '50%', border: '3px solid #111',
            boxShadow: `0 0 10px ${statusColor}`
          }} />
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.8rem', color: 'white', fontWeight: '700', letterSpacing: '-0.5px' }}>{athlete.name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <span style={{ 
              display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.8rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px',
              background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}40`
            }}>
              {dynamicStatus}
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Última act: <strong style={{color: 'rgba(255,255,255,0.7)'}}>{athlete.lastActivity || '--'}</strong>
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
            <button 
              onClick={() => setShowNotes(true)}
              style={{ 
                padding: '0.4rem 1rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500',
                background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s',
                backdropFilter: 'blur(10px)'
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
            >
              <NotebookPen size={14} /> Notas
            </button>
            <button 
              onClick={() => setShowTagsModal(true)}
              style={{ 
                padding: '0.4rem 1rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500',
                background: 'rgba(139, 92, 246, 0.1)', color: '#d8b4fe', border: '1px solid rgba(139, 92, 246, 0.2)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s',
                backdropFilter: 'blur(10px)'
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'; e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)'; e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)'; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z"/><path d="M7 7h.01"/></svg>
              Etiquetas
            </button>
            <button 
              onClick={() => setShowProfileModal(true)}
              style={{ 
                padding: '0.4rem 1rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600',
                background: 'var(--strava-orange)', color: 'white', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s',
                boxShadow: '0 4px 15px rgba(255, 87, 34, 0.2)'
              }}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 87, 34, 0.3)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 87, 34, 0.2)'; }}
            >
              <UserCircle size={14} /> Ficha
            </button>
          </div>
        </div>
      </div>

      {/* Columna 2: Fisiología Rápida (Micro-cards) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '0.8rem', zIndex: 1, alignItems: 'center', flex: '2 1 400px' }}>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)' }}>
            <Activity size={14} />
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Predisp. Matutina</span>
          </div>
          <span style={{ fontSize: '1.6rem', fontWeight: '700', color: statusColor, letterSpacing: '-0.5px' }}>{readiness > 0 ? `${readiness}%` : '--'}</span>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)' }}>
            <Zap size={14} />
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Impacto de Hoy</span>
          </div>
          <span style={{ fontSize: '1.4rem', fontWeight: '700', color: athlete.lastActivity?.toLowerCase().includes('hoy') ? '#f59e0b' : 'var(--health-green)', letterSpacing: '-0.5px' }}>
            {athlete.lastActivity?.toLowerCase().includes('hoy') ? 'Medio' : 'Bajo'}
          </span>
        </div>
        
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)' }}>
            <Heart size={14} />
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>HRV</span>
          </div>
          <span style={{ fontSize: '1.4rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>{hrvDisplay}</span>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)' }}>
            <Moon size={14} />
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Sueño</span>
          </div>
          <span style={{ fontSize: '1.2rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>{sleepDisplay}</span>
        </div>
        
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Carga CTL</span>
          </div>
          <span style={{ fontSize: '1.4rem', fontWeight: '700', color: 'var(--health-cyan)', letterSpacing: '-0.5px' }}>{athlete.ctl !== undefined ? athlete.ctl : '--'}</span>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Fatiga ATL</span>
          </div>
          <span style={{ fontSize: '1.4rem', fontWeight: '700', color: 'var(--strava-orange)', letterSpacing: '-0.5px' }}>{athlete.atl !== undefined ? athlete.atl : '--'}</span>
        </div>
      </div>

      {/* Columna 3: Veredicto IA Premium */}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 1, flex: '1 1 300px' }}>
        <div style={{ 
          background: 'rgba(0,0,0,0.3)', padding: '1.2rem', borderRadius: '16px', borderTop: `2px solid ${aiColor}`,
          boxShadow: dynamicStatus === 'Fatiga Alta' ? '0 10px 30px rgba(239, 68, 68, 0.15)' : 'inset 0 1px 0 rgba(255,255,255,0.05)',
          animation: dynamicStatus === 'Fatiga Alta' ? 'pulseBorder 2s infinite' : 'none',
          backdropFilter: 'blur(10px)'
        }}>
          <h3 style={{ margin: '0 0 0.8rem 0', fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
            <AiIcon size={16} color={aiColor} />
            Análisis de IA
          </h3>
          <p style={{ margin: 0, fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)', lineHeight: '1.6', fontWeight: '400', marginBottom: '1rem' }}>
            {aiVerdict}
          </p>
          {onAiClick && (
            <button 
              onClick={onAiClick}
              style={{
                background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.4)',
                borderRadius: '8px', padding: '0.6rem 1rem',
                color: 'white', fontWeight: 'bold', fontSize: '0.85rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                cursor: 'pointer', transition: 'all 0.2s', width: '100%',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.1)'
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Zap size={14} color="var(--health-green)" /> ✨ Análisis Biométrico IA Profundo
            </button>
          )}
        </div>
      </div>

      <CoachNotesModal 
        isOpen={showNotes} 
        onClose={() => setShowNotes(false)} 
        athlete={athlete} 
      />

      <AthleteProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        athleteId={athlete?.id || athlete?.clientId}
      />

      {showTagsModal && (
        <AthleteTagsModal
          athlete={{ ...athlete, tags: athleteTags }}
          onClose={() => setShowTagsModal(false)}
          onSave={(newTags) => {
            setShowTagsModal(false);
            setAthleteTags(newTags);
          }}
        />
      )}
      
      <style>{`
        @keyframes pulseBorder {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
      `}</style>
    </div>
  );
};

export default AthleteHeader;
